// Source Ingestion Architecture — source model, product/offer normalization, coverage intelligence

// ─── Source Classes ──────────────────────────────────────────────────────────

export type SourceClass = "must-have" | "opportunistic" | "intelligence";
export type SourceType = "marketplace" | "retailer" | "brand-direct" | "specialist" | "regional" | "club" | "intelligence";
export type SourceStatus = "live" | "partial" | "simulated" | "stale" | "unavailable";

export interface SourceProfile {
  id: string;
  name: string;
  sourceClass: SourceClass;
  sourceType: SourceType;
  status: SourceStatus;
  trustBaseline: number; // 0-100
  pricingReliability: number; // 0-100
  deliveryReliability: number; // 0-100
  freshness: "real-time" | "daily" | "weekly" | "stale";
  markets: string[]; // country codes
  categories: string[]; // product types covered
  strengths: string[];
  risks: string[];
}

// ─── Source Registry ─────────────────────────────────────────────────────────

export const SOURCE_REGISTRY: SourceProfile[] = [
  // ── Class 1: Must-Have ──
  {
    id: "amazon-us", name: "Amazon US", sourceClass: "must-have", sourceType: "marketplace",
    status: "partial", trustBaseline: 88, pricingReliability: 85, deliveryReliability: 95,
    freshness: "daily", markets: ["US"], categories: ["monitor", "office chair", "vacuum", "headphones", "earbuds", "espresso machine", "air fryer", "protein powder", "skincare device"],
    strengths: ["Fastest delivery", "Easy returns", "Prime eligible", "Wide selection"],
    risks: ["Third-party seller variance", "Counterfeit risk on some categories", "Price manipulation patterns"],
  },
  {
    id: "bestbuy-us", name: "Best Buy", sourceClass: "must-have", sourceType: "retailer",
    status: "partial", trustBaseline: 92, pricingReliability: 90, deliveryReliability: 88,
    freshness: "daily", markets: ["US"], categories: ["monitor", "headphones", "vacuum", "earbuds"],
    strengths: ["Price match guarantee", "In-store pickup", "Authorized dealer", "Strong return policy"],
    risks: ["Limited selection vs marketplace", "Higher base prices on some SKUs"],
  },
  {
    id: "bhphoto", name: "B&H Photo", sourceClass: "must-have", sourceType: "specialist",
    status: "partial", trustBaseline: 94, pricingReliability: 92, deliveryReliability: 85,
    freshness: "daily", markets: ["US"], categories: ["monitor", "headphones", "camera"],
    strengths: ["No tax in most states", "Expert curation", "Trusted electronics authority"],
    risks: ["Closed on Saturdays", "Slower shipping than Amazon"],
  },
  {
    id: "newegg", name: "Newegg", sourceClass: "must-have", sourceType: "specialist",
    status: "simulated", trustBaseline: 82, pricingReliability: 80, deliveryReliability: 78,
    freshness: "weekly", markets: ["US"], categories: ["monitor", "headphones"],
    strengths: ["Deep tech catalog", "Combo deals", "Open-box options"],
    risks: ["Mixed third-party seller quality", "Return restocking fees"],
  },

  // ── Brands Direct ──
  {
    id: "secretlab", name: "Secretlab Official", sourceClass: "must-have", sourceType: "brand-direct",
    status: "partial", trustBaseline: 96, pricingReliability: 98, deliveryReliability: 88,
    freshness: "weekly", markets: ["US", "GB", "CA", "AU"], categories: ["office chair"],
    strengths: ["Direct warranty", "Authentic guarantee", "Free shipping"],
    risks: ["No price competition", "Limited product range"],
  },
  {
    id: "hermanmiller", name: "Herman Miller", sourceClass: "must-have", sourceType: "brand-direct",
    status: "simulated", trustBaseline: 99, pricingReliability: 95, deliveryReliability: 90,
    freshness: "weekly", markets: ["US", "GB", "CA"], categories: ["office chair"],
    strengths: ["12-year warranty", "Authorized direct", "Premium build guarantee"],
    risks: ["Premium pricing", "No discounting"],
  },
  {
    id: "dyson", name: "Dyson Official", sourceClass: "must-have", sourceType: "brand-direct",
    status: "partial", trustBaseline: 97, pricingReliability: 96, deliveryReliability: 90,
    freshness: "weekly", markets: ["US", "GB", "CA", "AU"], categories: ["vacuum"],
    strengths: ["Full warranty", "Authentic product", "Refurb deals available"],
    risks: ["No third-party price competition", "Limited promo frequency"],
  },
  {
    id: "apple", name: "Apple Store", sourceClass: "must-have", sourceType: "brand-direct",
    status: "partial", trustBaseline: 99, pricingReliability: 99, deliveryReliability: 95,
    freshness: "daily", markets: ["US", "GB", "CA", "AU", "DE", "JP"], categories: ["headphones", "earbuds"],
    strengths: ["Official warranty", "Free engraving", "Trade-in program"],
    risks: ["No discounting", "Premium pricing"],
  },
  {
    id: "sony-direct", name: "Sony Store", sourceClass: "opportunistic", sourceType: "brand-direct",
    status: "simulated", trustBaseline: 95, pricingReliability: 90, deliveryReliability: 85,
    freshness: "weekly", markets: ["US"], categories: ["headphones", "earbuds"],
    strengths: ["Direct warranty", "Bundles available"],
    risks: ["Slower delivery than Amazon", "Limited stock"],
  },

  // ── Class 2: Opportunistic ──
  {
    id: "walmart", name: "Walmart", sourceClass: "opportunistic", sourceType: "retailer",
    status: "simulated", trustBaseline: 80, pricingReliability: 82, deliveryReliability: 78,
    freshness: "weekly", markets: ["US"], categories: ["vacuum", "air fryer", "headphones", "monitor", "office chair", "protein powder"],
    strengths: ["Lowest price on basics", "Walmart+ shipping", "In-store pickup"],
    risks: ["Mixed product quality", "Weaker return on some items"],
  },
  {
    id: "ebay", name: "eBay", sourceClass: "opportunistic", sourceType: "marketplace",
    status: "simulated", trustBaseline: 72, pricingReliability: 70, deliveryReliability: 68,
    freshness: "weekly", markets: ["US", "GB", "AU", "DE", "CA"],
    categories: ["monitor", "headphones", "office chair", "vacuum"],
    strengths: ["Buyer protection", "Open-box deals", "Used/refurbished options"],
    risks: ["Third-party seller variance", "Counterfeit risk", "Return complexity"],
  },
  {
    id: "costco", name: "Costco", sourceClass: "opportunistic", sourceType: "club",
    status: "simulated", trustBaseline: 90, pricingReliability: 88, deliveryReliability: 85,
    freshness: "weekly", markets: ["US", "CA"],
    categories: ["monitor", "vacuum", "headphones", "air fryer", "protein powder"],
    strengths: ["Extended return policy", "Member pricing", "Bulk value", "Kirkland quality"],
    risks: ["Membership required", "Limited selection per category"],
  },
  {
    id: "target", name: "Target", sourceClass: "opportunistic", sourceType: "retailer",
    status: "simulated", trustBaseline: 82, pricingReliability: 80, deliveryReliability: 82,
    freshness: "weekly", markets: ["US"],
    categories: ["vacuum", "air fryer", "headphones", "skincare device"],
    strengths: ["Same-day pickup", "Target Circle deals", "Clean returns"],
    risks: ["Limited tech depth", "Higher prices than Amazon on electronics"],
  },
  {
    id: "aliexpress", name: "AliExpress", sourceClass: "opportunistic", sourceType: "marketplace",
    status: "simulated", trustBaseline: 55, pricingReliability: 60, deliveryReliability: 45,
    freshness: "weekly", markets: ["US", "GB", "CA", "AU", "DE", "FR"],
    categories: ["monitor", "headphones", "vacuum"],
    strengths: ["Lowest prices globally", "Direct factory pricing", "Buyer protection"],
    risks: ["Long shipping (15-45 days)", "Quality inconsistency", "Counterfeit risk", "Difficult returns"],
  },
  {
    id: "temu", name: "Temu", sourceClass: "opportunistic", sourceType: "marketplace",
    status: "simulated", trustBaseline: 45, pricingReliability: 50, deliveryReliability: 40,
    freshness: "weekly", markets: ["US", "CA", "GB", "AU"],
    categories: ["vacuum", "air fryer", "headphones"],
    strengths: ["Ultra-low prices", "Free shipping deals"],
    risks: ["Unverified quality", "Long shipping", "No brand accountability", "Misleading images"],
  },
  {
    id: "homedepot", name: "Home Depot", sourceClass: "opportunistic", sourceType: "retailer",
    status: "simulated", trustBaseline: 85, pricingReliability: 84, deliveryReliability: 80,
    freshness: "weekly", markets: ["US"], categories: ["vacuum"],
    strengths: ["Pro-grade selection", "In-store pickup", "Installation services"],
    risks: ["Limited tech catalog", "Appliance-focused"],
  },
  {
    id: "crutchfield", name: "Crutchfield", sourceClass: "opportunistic", sourceType: "specialist",
    status: "simulated", trustBaseline: 90, pricingReliability: 88, deliveryReliability: 82,
    freshness: "weekly", markets: ["US"], categories: ["headphones", "earbuds", "speaker"],
    strengths: ["Free extended warranty", "Audio specialist expertise", "Lifetime tech support"],
    risks: ["Higher prices than Amazon", "Smaller selection"],
  },
  {
    id: "officedepot", name: "Office Depot", sourceClass: "opportunistic", sourceType: "retailer",
    status: "simulated", trustBaseline: 78, pricingReliability: 76, deliveryReliability: 72,
    freshness: "weekly", markets: ["US"], categories: ["office chair"],
    strengths: ["Business pricing", "Bulk discounts", "Local pickup"],
    risks: ["Slower delivery", "Limited premium selection"],
  },

  // ── Local Dominant Retailers ──
  {
    id: "jbhifi", name: "JB Hi-Fi", sourceClass: "must-have", sourceType: "retailer",
    status: "simulated", trustBaseline: 88, pricingReliability: 85, deliveryReliability: 82,
    freshness: "weekly", markets: ["AU", "NZ"],
    categories: ["monitor", "headphones", "vacuum"],
    strengths: ["Australia's top electronics retailer", "Price match", "In-store pickup"],
    risks: ["AU pricing premium", "Limited online delivery speed"],
  },
  {
    id: "harveynorman", name: "Harvey Norman", sourceClass: "opportunistic", sourceType: "retailer",
    status: "simulated", trustBaseline: 82, pricingReliability: 78, deliveryReliability: 76,
    freshness: "weekly", markets: ["AU", "NZ"],
    categories: ["monitor", "vacuum", "air fryer"],
    strengths: ["Broad electronics range", "Finance options", "In-store expert help"],
    risks: ["Higher prices", "Aggressive upselling"],
  },
  {
    id: "johnlewis", name: "John Lewis", sourceClass: "must-have", sourceType: "retailer",
    status: "simulated", trustBaseline: 92, pricingReliability: 88, deliveryReliability: 86,
    freshness: "weekly", markets: ["GB"],
    categories: ["monitor", "vacuum", "headphones", "office chair", "espresso machine"],
    strengths: ["2-year guarantee", "Never knowingly undersold", "Premium service"],
    risks: ["Smaller tech range", "Slightly higher prices"],
  },
  {
    id: "argos", name: "Argos", sourceClass: "opportunistic", sourceType: "retailer",
    status: "simulated", trustBaseline: 80, pricingReliability: 78, deliveryReliability: 84,
    freshness: "weekly", markets: ["GB"],
    categories: ["vacuum", "air fryer", "headphones"],
    strengths: ["Same-day collection", "Widespread UK stores", "Competitive pricing"],
    risks: ["Limited premium range", "Basic customer service"],
  },
  {
    id: "canadacomputers", name: "Canada Computers", sourceClass: "opportunistic", sourceType: "specialist",
    status: "simulated", trustBaseline: 84, pricingReliability: 82, deliveryReliability: 78,
    freshness: "weekly", markets: ["CA"],
    categories: ["monitor", "headphones"],
    strengths: ["Canadian specialist", "Price match", "Local stores"],
    risks: ["Ontario-centric", "Smaller national footprint"],
  },
  {
    id: "mediamarkt", name: "MediaMarkt", sourceClass: "must-have", sourceType: "retailer",
    status: "simulated", trustBaseline: 84, pricingReliability: 82, deliveryReliability: 80,
    freshness: "weekly", markets: ["DE", "ES", "NL", "BE", "AT"],
    categories: ["monitor", "vacuum", "headphones", "espresso machine"],
    strengths: ["Largest European electronics retailer", "Price match", "Local stores"],
    risks: ["Varying quality by market", "Online experience lags"],
  },
  {
    id: "otto", name: "Otto", sourceClass: "opportunistic", sourceType: "retailer",
    status: "simulated", trustBaseline: 80, pricingReliability: 78, deliveryReliability: 82,
    freshness: "weekly", markets: ["DE"],
    categories: ["vacuum", "air fryer", "office chair"],
    strengths: ["Germany's second-largest online retailer", "Good return policy"],
    risks: ["Higher prices on electronics", "Slower than Amazon DE"],
  },
  {
    id: "flipkart", name: "Flipkart", sourceClass: "must-have", sourceType: "marketplace",
    status: "simulated", trustBaseline: 78, pricingReliability: 76, deliveryReliability: 80,
    freshness: "weekly", markets: ["IN"],
    categories: ["monitor", "headphones", "vacuum", "air fryer"],
    strengths: ["India's largest marketplace", "Flash sales", "COD available"],
    risks: ["Third-party variance", "Return process varies"],
  },
  {
    id: "noon", name: "Noon", sourceClass: "must-have", sourceType: "marketplace",
    status: "simulated", trustBaseline: 76, pricingReliability: 74, deliveryReliability: 78,
    freshness: "weekly", markets: ["AE", "SA"],
    categories: ["monitor", "headphones", "vacuum", "air fryer"],
    strengths: ["UAE/Saudi dominant platform", "Same-day delivery", "Local returns"],
    risks: ["Smaller catalog", "Third-party seller quality varies"],
  },

  // ── International ──
  {
    id: "amazon-uk", name: "Amazon UK", sourceClass: "must-have", sourceType: "marketplace",
    status: "simulated", trustBaseline: 86, pricingReliability: 84, deliveryReliability: 90,
    freshness: "weekly", markets: ["GB"], categories: ["monitor", "office chair", "vacuum", "headphones"],
    strengths: ["Prime UK", "Easy returns", "Wide selection"],
    risks: ["Third-party variance", "VAT included in price"],
  },
  {
    id: "currys", name: "Currys", sourceClass: "must-have", sourceType: "retailer",
    status: "simulated", trustBaseline: 84, pricingReliability: 82, deliveryReliability: 80,
    freshness: "weekly", markets: ["GB"], categories: ["monitor", "vacuum", "headphones"],
    strengths: ["UK specialist", "In-store pickup", "Price match"],
    risks: ["Mixed online reviews", "Delivery delays reported"],
  },
  {
    id: "amazon-ca", name: "Amazon CA", sourceClass: "must-have", sourceType: "marketplace",
    status: "simulated", trustBaseline: 85, pricingReliability: 82, deliveryReliability: 88,
    freshness: "weekly", markets: ["CA"], categories: ["monitor", "office chair", "vacuum", "headphones"],
    strengths: ["Prime CA", "Easy returns"],
    risks: ["Smaller selection than US", "Cross-border fulfillment delays"],
  },

  // ── Class 3: Intelligence Only ──
  {
    id: "camelcamelcamel", name: "CamelCamelCamel", sourceClass: "intelligence", sourceType: "intelligence",
    status: "simulated", trustBaseline: 75, pricingReliability: 70, deliveryReliability: 0,
    freshness: "daily", markets: ["US", "GB", "CA", "DE"], categories: ["monitor", "office chair", "vacuum", "headphones"],
    strengths: ["Historical price data", "Price drop alerts", "Fake discount detection"],
    risks: ["Amazon-only coverage", "No purchase capability"],
  },
  {
    id: "fakespot", name: "Fakespot", sourceClass: "intelligence", sourceType: "intelligence",
    status: "simulated", trustBaseline: 70, pricingReliability: 0, deliveryReliability: 0,
    freshness: "weekly", markets: ["US"], categories: ["monitor", "office chair", "vacuum", "headphones"],
    strengths: ["Review authenticity analysis", "Seller quality scoring"],
    risks: ["Signal quality varies by category", "Not exhaustive"],
  },
];

// ─── Product Identity ────────────────────────────────────────────────────────

export interface ProductIdentity {
  id: string;
  brand: string;
  model: string;
  variant?: string;
  category: string;
  productType: string;
  keyAttributes: Record<string, string>;
  normalizedName: string;
}

// ─── Offer Identity ──────────────────────────────────────────────────────────

export interface OfferIdentity {
  id: string;
  productId: string;
  sourceId: string;
  sourceName: string;
  sourceType: SourceType;
  listedPrice: number;
  shippingCost: number;
  estimatedTax: number;
  landedCost: number;
  currency: string;
  trustStatus: "verified" | "likely-authentic" | "unverified" | "risky";
  deliveryEstimate: string;
  deliveryReliability: "confirmed" | "estimated" | "uncertain";
  availability: "in-stock" | "limited" | "backorder" | "unknown";
  freshness: "real-time" | "recent" | "stale";
  routeType: "brand-direct" | "authorized-retailer" | "major-marketplace" | "third-party" | "unknown";
  promoActive: boolean;
  promoCredibility: "verified" | "suspicious" | "unknown";
  url: string;
}

// ─── Normalization Engine ────────────────────────────────────────────────────

const PRODUCT_CATALOG: ProductIdentity[] = [
  // Monitors
  { id: "prod-samsung-g7-32", brand: "Samsung", model: "Odyssey G7", variant: "32\"", category: "electronics", productType: "monitor", keyAttributes: { resolution: "2560x1440", refreshRate: "165Hz", panel: "VA", hdr: "HDR600" }, normalizedName: "Samsung Odyssey G7 32\"" },
  { id: "prod-gigabyte-m27q-x", brand: "Gigabyte", model: "M27Q X", category: "electronics", productType: "monitor", keyAttributes: { resolution: "2560x1440", refreshRate: "170Hz", panel: "IPS", kvm: "Yes" }, normalizedName: "Gigabyte M27Q X" },
  { id: "prod-lg-27gp950", brand: "LG", model: "UltraGear 27GP950-B", category: "electronics", productType: "monitor", keyAttributes: { resolution: "3840x2160", refreshRate: "160Hz", panel: "Nano IPS", hdmi: "2.1" }, normalizedName: "LG UltraGear 27GP950-B" },
  { id: "prod-asus-pa278qv", brand: "ASUS", model: "ProArt PA278QV", category: "electronics", productType: "monitor", keyAttributes: { resolution: "2560x1440", panel: "IPS", colorAccuracy: "Delta E<2", calFactory: "Yes" }, normalizedName: "ASUS ProArt PA278QV" },
  { id: "prod-dell-u2723qe", brand: "Dell", model: "UltraSharp U2723QE", category: "electronics", productType: "monitor", keyAttributes: { resolution: "3840x2160", panel: "IPS Black", usbc: "90W" }, normalizedName: "Dell UltraSharp U2723QE" },

  // Office Chairs
  { id: "prod-secretlab-titan", brand: "Secretlab", model: "Titan Evo", category: "furniture", productType: "office chair", keyAttributes: { lumbar: "4-way", foam: "Cold-cure", warranty: "5 years" }, normalizedName: "Secretlab Titan Evo" },
  { id: "prod-hon-ignition", brand: "HON", model: "Ignition 2.0", category: "furniture", productType: "office chair", keyAttributes: { back: "Mesh", arms: "Adjustable", warranty: "11 years" }, normalizedName: "HON Ignition 2.0" },
  { id: "prod-hm-aeron", brand: "Herman Miller", model: "Aeron", variant: "Remastered", category: "furniture", productType: "office chair", keyAttributes: { lumbar: "PostureFit SL", mesh: "8Z Pellicle", warranty: "12 years" }, normalizedName: "Herman Miller Aeron (Remastered)" },
  { id: "prod-steelcase-leap", brand: "Steelcase", model: "Leap V2", category: "furniture", productType: "office chair", keyAttributes: { back: "LiveBack flex", arms: "4D", warranty: "12 years" }, normalizedName: "Steelcase Leap V2" },

  // Vacuums
  { id: "prod-dyson-v15", brand: "Dyson", model: "V15 Detect Absolute", category: "appliances", productType: "vacuum", keyAttributes: { type: "Cordless stick", laser: "Yes", suction: "230AW", runtime: "60min" }, normalizedName: "Dyson V15 Detect Absolute" },
  { id: "prod-shark-nv352", brand: "Shark", model: "Navigator Lift-Away NV352", category: "appliances", productType: "vacuum", keyAttributes: { type: "Upright corded", liftAway: "Yes", filtration: "HEPA" }, normalizedName: "Shark Navigator Lift-Away NV352" },
  { id: "prod-bissell-crosswave", brand: "Bissell", model: "CrossWave Pet Pro", category: "appliances", productType: "vacuum", keyAttributes: { type: "Multi-surface", wetDry: "Yes", petFocused: "Yes" }, normalizedName: "Bissell CrossWave Pet Pro" },
  { id: "prod-roborock-s8", brand: "Roborock", model: "S8 Pro Ultra", category: "appliances", productType: "vacuum", keyAttributes: { type: "Robot", mop: "Yes", selfEmpty: "Yes", lidar: "Yes" }, normalizedName: "Roborock S8 Pro Ultra" },

  // Headphones
  { id: "prod-sony-xm5", brand: "Sony", model: "WH-1000XM5", category: "audio", productType: "headphones", keyAttributes: { anc: "Industry-leading", battery: "30h", multipoint: "Yes" }, normalizedName: "Sony WH-1000XM5" },
  { id: "prod-soundcore-q45", brand: "Soundcore", model: "Space Q45", category: "audio", productType: "headphones", keyAttributes: { anc: "Adaptive", battery: "50h", codec: "LDAC" }, normalizedName: "Soundcore Space Q45" },
  { id: "prod-airpods-max", brand: "Apple", model: "AirPods Max", variant: "USB-C", category: "audio", productType: "headphones", keyAttributes: { anc: "Computational", spatialAudio: "Yes", build: "Aluminum" }, normalizedName: "Apple AirPods Max (USB-C)" },
  { id: "prod-bose-qc-ultra", brand: "Bose", model: "QuietComfort Ultra", category: "audio", productType: "headphones", keyAttributes: { anc: "CustomTune", spatialAudio: "Yes", battery: "24h" }, normalizedName: "Bose QuietComfort Ultra" },
];

// ─── Offer Catalog (Simulated) ───────────────────────────────────────────────

export const OFFER_CATALOG: OfferIdentity[] = [
  // Samsung Odyssey G7 32"
  { id: "offer-g7-amazon", productId: "prod-samsung-g7-32", sourceId: "amazon-us", sourceName: "Amazon US", sourceType: "marketplace", listedPrice: 249, shippingCost: 0, estimatedTax: 22, landedCost: 271, currency: "USD", trustStatus: "verified", deliveryEstimate: "2 days", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "major-marketplace", promoActive: false, promoCredibility: "unknown", url: "https://www.amazon.com/dp/B088HJ4VQK" },
  { id: "offer-g7-bhphoto", productId: "prod-samsung-g7-32", sourceId: "bhphoto", sourceName: "B&H Photo", sourceType: "specialist", listedPrice: 249, shippingCost: 0, estimatedTax: 0, landedCost: 249, currency: "USD", trustStatus: "verified", deliveryEstimate: "3 days", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "authorized-retailer", promoActive: false, promoCredibility: "unknown", url: "https://www.bhphotovideo.com/c/product/1576936-REG/samsung_lc32g75tqsnxza_32_odyssey_g7_gaming.html" },
  { id: "offer-g7-bestbuy", productId: "prod-samsung-g7-32", sourceId: "bestbuy-us", sourceName: "Best Buy", sourceType: "retailer", listedPrice: 249, shippingCost: 0, estimatedTax: 22, landedCost: 271, currency: "USD", trustStatus: "verified", deliveryEstimate: "2 days", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "authorized-retailer", promoActive: false, promoCredibility: "unknown", url: "https://www.bestbuy.com/site/samsung-odyssey-g7-32-led-curved-qhd-freesync-monitor/6457446.p" },

  // Secretlab Titan Evo
  { id: "offer-titan-secretlab", productId: "prod-secretlab-titan", sourceId: "secretlab", sourceName: "Secretlab Official", sourceType: "brand-direct", listedPrice: 449, shippingCost: 0, estimatedTax: 40, landedCost: 489, currency: "USD", trustStatus: "verified", deliveryEstimate: "3-5 days", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "brand-direct", promoActive: false, promoCredibility: "unknown", url: "https://secretlab.co/collections/titan-evo" },
  { id: "offer-titan-amazon", productId: "prod-secretlab-titan", sourceId: "amazon-us", sourceName: "Amazon US", sourceType: "marketplace", listedPrice: 449, shippingCost: 0, estimatedTax: 40, landedCost: 489, currency: "USD", trustStatus: "likely-authentic", deliveryEstimate: "2 days", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "major-marketplace", promoActive: false, promoCredibility: "unknown", url: "https://www.amazon.com/dp/B09KN2WN4F" },

  // Dyson V15
  { id: "offer-v15-dyson", productId: "prod-dyson-v15", sourceId: "dyson", sourceName: "Dyson Official", sourceType: "brand-direct", listedPrice: 279, shippingCost: 0, estimatedTax: 25, landedCost: 304, currency: "USD", trustStatus: "verified", deliveryEstimate: "2 days", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "brand-direct", promoActive: false, promoCredibility: "unknown", url: "https://www.dyson.com/vacuum-cleaners/cordless/v15/detect-absolute-gold-gold" },
  { id: "offer-v15-bestbuy", productId: "prod-dyson-v15", sourceId: "bestbuy-us", sourceName: "Best Buy", sourceType: "retailer", listedPrice: 279, shippingCost: 0, estimatedTax: 25, landedCost: 304, currency: "USD", trustStatus: "verified", deliveryEstimate: "Same day", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "authorized-retailer", promoActive: false, promoCredibility: "unknown", url: "https://www.bestbuy.com/site/dyson-v15-detect-absolute/6539767.p" },
  { id: "offer-v15-walmart", productId: "prod-dyson-v15", sourceId: "walmart", sourceName: "Walmart", sourceType: "retailer", listedPrice: 279, shippingCost: 0, estimatedTax: 22, landedCost: 301, currency: "USD", trustStatus: "verified", deliveryEstimate: "3 days", deliveryReliability: "estimated", availability: "in-stock", freshness: "recent", routeType: "authorized-retailer", promoActive: false, promoCredibility: "unknown", url: "https://www.walmart.com/ip/Dyson-V15-Detect-Absolute/1234567890" },

  // Sony WH-1000XM5
  { id: "offer-xm5-amazon", productId: "prod-sony-xm5", sourceId: "amazon-us", sourceName: "Amazon US", sourceType: "marketplace", listedPrice: 328, shippingCost: 0, estimatedTax: 29, landedCost: 357, currency: "USD", trustStatus: "verified", deliveryEstimate: "Next day", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "major-marketplace", promoActive: false, promoCredibility: "unknown", url: "https://www.amazon.com/dp/B09XS7JWHH" },
  { id: "offer-xm5-bestbuy", productId: "prod-sony-xm5", sourceId: "bestbuy-us", sourceName: "Best Buy", sourceType: "retailer", listedPrice: 328, shippingCost: 0, estimatedTax: 29, landedCost: 357, currency: "USD", trustStatus: "verified", deliveryEstimate: "2 days", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "authorized-retailer", promoActive: false, promoCredibility: "unknown", url: "https://www.bestbuy.com/site/sony-wh-1000xm5/6505727.p" },
  { id: "offer-xm5-crutchfield", productId: "prod-sony-xm5", sourceId: "crutchfield", sourceName: "Crutchfield", sourceType: "specialist", listedPrice: 328, shippingCost: 0, estimatedTax: 0, landedCost: 328, currency: "USD", trustStatus: "verified", deliveryEstimate: "3-5 days", deliveryReliability: "estimated", availability: "in-stock", freshness: "stale", routeType: "authorized-retailer", promoActive: false, promoCredibility: "unknown", url: "https://www.crutchfield.com/p_158WH1KXM5/Sony-WH-1000XM5.html" },

  // Soundcore Space Q45
  { id: "offer-q45-amazon", productId: "prod-soundcore-q45", sourceId: "amazon-us", sourceName: "Amazon US", sourceType: "marketplace", listedPrice: 129, shippingCost: 0, estimatedTax: 11, landedCost: 140, currency: "USD", trustStatus: "verified", deliveryEstimate: "2 days", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "major-marketplace", promoActive: false, promoCredibility: "unknown", url: "https://www.amazon.com/dp/B0C6KHZ6HG" },

  // Apple AirPods Max
  { id: "offer-apm-apple", productId: "prod-airpods-max", sourceId: "apple", sourceName: "Apple Store", sourceType: "brand-direct", listedPrice: 549, shippingCost: 0, estimatedTax: 49, landedCost: 598, currency: "USD", trustStatus: "verified", deliveryEstimate: "Next day", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "brand-direct", promoActive: false, promoCredibility: "unknown", url: "https://www.apple.com/shop/buy-airpods/airpods-max" },
  { id: "offer-apm-bestbuy", productId: "prod-airpods-max", sourceId: "bestbuy-us", sourceName: "Best Buy", sourceType: "retailer", listedPrice: 549, shippingCost: 0, estimatedTax: 49, landedCost: 598, currency: "USD", trustStatus: "verified", deliveryEstimate: "Same day", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "authorized-retailer", promoActive: false, promoCredibility: "unknown", url: "https://www.bestbuy.com/site/apple-airpods-max-usb-c/6534270.p" },

  // Herman Miller Aeron
  { id: "offer-aeron-hm", productId: "prod-hm-aeron", sourceId: "hermanmiller", sourceName: "Herman Miller", sourceType: "brand-direct", listedPrice: 1395, shippingCost: 0, estimatedTax: 125, landedCost: 1520, currency: "USD", trustStatus: "verified", deliveryEstimate: "2 days", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "brand-direct", promoActive: false, promoCredibility: "unknown", url: "https://www.hermanmiller.com/products/seating/office-chairs/aeron-chairs/" },
  { id: "offer-aeron-amazon", productId: "prod-hm-aeron", sourceId: "amazon-us", sourceName: "Amazon US", sourceType: "marketplace", listedPrice: 1295, shippingCost: 0, estimatedTax: 116, landedCost: 1411, currency: "USD", trustStatus: "likely-authentic", deliveryEstimate: "3 days", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "major-marketplace", promoActive: false, promoCredibility: "unknown", url: "https://www.amazon.com/dp/B01DGM7ZII" },

  // HON Ignition 2.0
  { id: "offer-hon-amazon", productId: "prod-hon-ignition", sourceId: "amazon-us", sourceName: "Amazon US", sourceType: "marketplace", listedPrice: 249, shippingCost: 0, estimatedTax: 22, landedCost: 271, currency: "USD", trustStatus: "verified", deliveryEstimate: "3 days", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "major-marketplace", promoActive: false, promoCredibility: "unknown", url: "https://www.amazon.com/dp/B07L38TM4F" },

  // Shark Navigator
  { id: "offer-shark-amazon", productId: "prod-shark-nv352", sourceId: "amazon-us", sourceName: "Amazon US", sourceType: "marketplace", listedPrice: 149, shippingCost: 0, estimatedTax: 13, landedCost: 162, currency: "USD", trustStatus: "verified", deliveryEstimate: "2 days", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "major-marketplace", promoActive: false, promoCredibility: "unknown", url: "https://www.amazon.com/dp/B005LT3BKE" },
  { id: "offer-shark-walmart", productId: "prod-shark-nv352", sourceId: "walmart", sourceName: "Walmart", sourceType: "retailer", listedPrice: 139, shippingCost: 0, estimatedTax: 12, landedCost: 151, currency: "USD", trustStatus: "verified", deliveryEstimate: "3 days", deliveryReliability: "estimated", availability: "in-stock", freshness: "recent", routeType: "authorized-retailer", promoActive: false, promoCredibility: "unknown", url: "https://www.walmart.com/ip/Shark-Navigator-Lift-Away-NV352/21901047" },

  // Bissell CrossWave
  { id: "offer-bissell-amazon", productId: "prod-bissell-crosswave", sourceId: "amazon-us", sourceName: "Amazon US", sourceType: "marketplace", listedPrice: 199, shippingCost: 0, estimatedTax: 18, landedCost: 217, currency: "USD", trustStatus: "verified", deliveryEstimate: "Same day", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "major-marketplace", promoActive: false, promoCredibility: "unknown", url: "https://www.amazon.com/dp/B0B54J4FLX" },

  // Gigabyte M27Q X
  { id: "offer-m27q-amazon", productId: "prod-gigabyte-m27q-x", sourceId: "amazon-us", sourceName: "Amazon US", sourceType: "marketplace", listedPrice: 129, shippingCost: 0, estimatedTax: 11, landedCost: 140, currency: "USD", trustStatus: "verified", deliveryEstimate: "2 days", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "major-marketplace", promoActive: false, promoCredibility: "unknown", url: "https://www.amazon.com/dp/B09VH32B8V" },
  { id: "offer-m27q-newegg", productId: "prod-gigabyte-m27q-x", sourceId: "newegg", sourceName: "Newegg", sourceType: "specialist", listedPrice: 129, shippingCost: 0, estimatedTax: 10, landedCost: 139, currency: "USD", trustStatus: "verified", deliveryEstimate: "3 days", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "authorized-retailer", promoActive: false, promoCredibility: "unknown", url: "https://www.newegg.com/p/N82E16824012048" },

  // LG UltraGear
  { id: "offer-lg-amazon", productId: "prod-lg-27gp950", sourceId: "amazon-us", sourceName: "Amazon US", sourceType: "marketplace", listedPrice: 399, shippingCost: 0, estimatedTax: 36, landedCost: 435, currency: "USD", trustStatus: "verified", deliveryEstimate: "Next day", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "major-marketplace", promoActive: false, promoCredibility: "unknown", url: "https://www.amazon.com/dp/B09QG4JRQS" },
  { id: "offer-lg-bestbuy", productId: "prod-lg-27gp950", sourceId: "bestbuy-us", sourceName: "Best Buy", sourceType: "retailer", listedPrice: 399, shippingCost: 0, estimatedTax: 36, landedCost: 435, currency: "USD", trustStatus: "verified", deliveryEstimate: "Next day", deliveryReliability: "confirmed", availability: "in-stock", freshness: "recent", routeType: "authorized-retailer", promoActive: false, promoCredibility: "unknown", url: "https://www.bestbuy.com/site/lg-ultragear-27-ips-led-4k-uhd/6451080.p" },
];

// ─── Source Intelligence Queries ─────────────────────────────────────────────

export function getSourcesForProduct(productId: string): OfferIdentity[] {
  return OFFER_CATALOG.filter(o => o.productId === productId);
}

export function getSourcesForCategory(productType: string, market?: string): SourceProfile[] {
  return SOURCE_REGISTRY.filter(s => {
    const catMatch = s.categories.includes(productType);
    const marketMatch = !market || s.markets.includes(market);
    return catMatch && marketMatch;
  });
}

export function getProductIdentity(normalizedName: string): ProductIdentity | undefined {
  return PRODUCT_CATALOG.find(p =>
    p.normalizedName.toLowerCase() === normalizedName.toLowerCase()
  );
}

export function getOffersForProduct(normalizedName: string): OfferIdentity[] {
  const product = getProductIdentity(normalizedName);
  if (!product) return [];
  return OFFER_CATALOG.filter(o => o.productId === product.id);
}

export function getBestOffer(normalizedName: string): OfferIdentity | undefined {
  const offers = getOffersForProduct(normalizedName);
  if (offers.length === 0) return undefined;

  // Sort by: trust > landed cost > delivery reliability
  return offers.sort((a, b) => {
    const trustOrder = { verified: 4, "likely-authentic": 3, unverified: 2, risky: 1 };
    const trustDiff = trustOrder[b.trustStatus] - trustOrder[a.trustStatus];
    if (trustDiff !== 0) return trustDiff;

    if (a.landedCost !== b.landedCost) return a.landedCost - b.landedCost;

    const reliabilityOrder = { confirmed: 3, estimated: 2, uncertain: 1 };
    return reliabilityOrder[b.deliveryReliability] - reliabilityOrder[a.deliveryReliability];
  })[0];
}

// ─── Source Coverage Intelligence ────────────────────────────────────────────

export interface CoverageAssessment {
  strength: "strong" | "moderate" | "weak" | "minimal";
  liveSourceCount: number;
  totalSourceCount: number;
  mustHaveCoverage: number; // 0-100
  bestSourceClass: SourceClass;
  gaps: string[];
}

export function assessCoverage(productType: string, market: string): CoverageAssessment {
  const sources = getSourcesForCategory(productType, market);
  const mustHaves = sources.filter(s => s.sourceClass === "must-have");
  const liveSources = sources.filter(s => s.status === "live" || s.status === "partial");

  const mustHaveCoverage = mustHaves.length > 0
    ? Math.round((mustHaves.filter(s => s.status !== "unavailable").length / Math.max(mustHaves.length, 1)) * 100)
    : 0;

  const gaps: string[] = [];
  if (mustHaves.length === 0) gaps.push("No primary sources available for this market");
  if (sources.filter(s => s.sourceType === "brand-direct").length === 0) gaps.push("No direct brand channel available");
  if (liveSources.length < 2) gaps.push("Limited real-time data coverage");

  let strength: CoverageAssessment["strength"];
  if (liveSources.length >= 3 && mustHaveCoverage >= 80) strength = "strong";
  else if (liveSources.length >= 2 && mustHaveCoverage >= 50) strength = "moderate";
  else if (liveSources.length >= 1) strength = "weak";
  else strength = "minimal";

  return {
    strength,
    liveSourceCount: liveSources.length,
    totalSourceCount: sources.length,
    mustHaveCoverage,
    bestSourceClass: mustHaves.length > 0 ? "must-have" : sources.length > 0 ? "opportunistic" : "intelligence",
    gaps,
  };
}

// ─── Source-Aware Audit Helpers ───────────────────────────────────────────────

export function getSourceAuditLine(productType: string, market: string): string {
  const assessment = assessCoverage(productType, market);
  const sources = getSourcesForCategory(productType, market);
  const sourceNames = sources
    .filter(s => s.sourceClass === "must-have" || s.sourceClass === "opportunistic")
    .slice(0, 4)
    .map(s => s.name);

  if (assessment.strength === "strong") {
    return `Verified across ${sourceNames.join(", ")} and ${sources.length - sourceNames.length} additional sources`;
  }
  if (assessment.strength === "moderate") {
    return `Checked ${sourceNames.join(", ")} with ${assessment.mustHaveCoverage}% primary source coverage`;
  }
  return `Based on available data from ${sourceNames.join(", ")}. Coverage is limited for this market.`;
}

export function getRouteTypeLabel(routeType: OfferIdentity["routeType"]): string {
  switch (routeType) {
    case "brand-direct": return "Direct from brand";
    case "authorized-retailer": return "Authorized retailer";
    case "major-marketplace": return "Major marketplace";
    case "third-party": return "Third-party seller";
    default: return "Unknown route";
  }
}
